import { useCallback, useEffect, useRef, useState } from 'react';
import type { ExportResult } from '../types';
import {
  buildExportRunLog,
  type ExportLogMode,
  type ExportLogResultInput,
  type ExportRunLog,
} from '../lib/exportRunLog';

interface RecordExportRunOptions {
  readonly mode: ExportLogMode;
  readonly outputPath: string;
}

export interface UseExportRunLogReturn {
  readonly log: ExportRunLog | null;
  readonly entryCount: number;
  readonly windowOpen: boolean;
  readonly failureNoticeOpen: boolean;
  readonly openWindow: () => void;
  readonly closeWindow: () => void;
  readonly dismissFailureNotice: () => void;
  readonly recordResult: (
    result: ExportLogResultInput,
    options: RecordExportRunOptions,
  ) => void;
}

export function useExportRunLog(result: ExportResult | null): UseExportRunLogReturn {
  const [log, setLog] = useState<ExportRunLog | null>(null);
  const [windowOpen, setWindowOpen] = useState(false);
  const [failureNoticeOpen, setFailureNoticeOpen] = useState(false);
  const capturedResultRef = useRef<ExportResult | null>(null);

  const recordResult = useCallback((
    nextResult: ExportLogResultInput,
    options: RecordExportRunOptions,
  ) => {
    const nextLog = buildExportRunLog(nextResult, {
      ...options,
      completedAt: new Date().toISOString(),
    });
    setLog(nextLog);
    setFailureNoticeOpen(nextLog.failureNoticeEntries.length > 0);
  }, []);

  useEffect(() => {
    if (!result || capturedResultRef.current === result) {
      return;
    }
    capturedResultRef.current = result;
    recordResult(result, {
      mode: 'table',
      outputPath: result.filePath ?? '',
    });
  }, [recordResult, result]);

  const openWindow = useCallback(() => setWindowOpen(true), []);
  const closeWindow = useCallback(() => setWindowOpen(false), []);
  const dismissFailureNotice = useCallback(() => setFailureNoticeOpen(false), []);

  return {
    log,
    entryCount: log?.entries.length ?? 0,
    windowOpen,
    failureNoticeOpen,
    openWindow,
    closeWindow,
    dismissFailureNotice,
    recordResult,
  };
}
